<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { apiGet } from "../api";

interface Money { amount: number }
interface OrderRef { id: string; orderNumber: string; status: string; total: Money; createdAt: string }
interface CustomerDetail {
  id: string; name?: string; email: string; city?: string; orderCount: number; totalSpent: Money; lastOrderAt?: string;
  recentOrders: OrderRef[]; defaultAddress?: Record<string, string>;
}

const euro = (c: number) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(c / 100);
const datum = (s?: string) => (s ? new Date(s).toLocaleDateString("nl-NL", { day: "numeric", month: "short" }) : "—");
const initials = (n?: string, e?: string) => (n || e || "?").slice(0, 2).toUpperCase();

const route = useRoute();
const router = useRouter();
const id = computed(() => route.params.id as string);
const detail = ref<CustomerDetail | null>(null);
const loading = ref(true);
const error = ref("");

onMounted(async () => {
  try { detail.value = await apiGet<CustomerDetail>(`/customers/${id.value}`, { auth: true }); }
  catch (e) { error.value = String(e); } finally { loading.value = false; }
});
</script>

<template>
  <div>
    <p v-if="loading" class="muted">Laden…</p>
    <p v-else-if="error" class="err">{{ error }}</p>
    <template v-else-if="detail">
      <header class="dhead">
        <button class="back" @click="router.push('/klanten')">←</button>
        <span class="avatar">{{ initials(detail.name, detail.email) }}</span>
        <div><h1 class="title">{{ detail.name || detail.email }}</h1><div class="sub">{{ detail.email }}{{ detail.city ? ' · ' + detail.city : '' }}</div></div>
      </header>
      <div class="kpis">
        <div class="kpi"><span class="kl">Bestellingen</span><strong>{{ detail.orderCount }}</strong></div>
        <div class="kpi"><span class="kl">Totaal besteed</span><strong>{{ euro(detail.totalSpent.amount) }}</strong></div>
        <div class="kpi"><span class="kl">Laatste bestelling</span><strong>{{ datum(detail.lastOrderAt) }}</strong></div>
      </div>
      <div class="dgrid">
        <div class="card">
          <h2 class="ct">Recente bestellingen</h2>
          <div v-for="o in detail.recentOrders" :key="o.id" class="line clickrow" @click="router.push(`/bestellingen/${o.id}`)">
            <span class="mono">{{ o.orderNumber }}</span><span class="sub">{{ datum(o.createdAt) }}</span><span>{{ euro(o.total.amount) }}</span>
          </div>
        </div>
        <div class="card"><h2 class="ct">Standaard adres</h2>
          <template v-if="detail.defaultAddress">{{ detail.defaultAddress.street }} {{ detail.defaultAddress.houseNumber }}<br />{{ detail.defaultAddress.postalCode }} {{ detail.defaultAddress.city }}</template>
          <span v-else class="sub">—</span>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.title { font-family: var(--font-ui); font-weight: 700; font-size: 24px; margin: 0; }
.muted { color: var(--muted); } .err { color: var(--red); }
.sub { color: var(--muted); font-size: 12px; }
.mono { font-family: monospace; }
.avatar { width: 44px; height: 44px; border-radius: 999px; background: var(--coral-wash); color: var(--coral-wash-text); display: grid; place-items: center; font-weight: 700; }
.dhead { display: flex; align-items: center; gap: 12px; margin-bottom: 18px; }
.back { font-size: 20px; border: 0; background: none; cursor: pointer; }
.kpis { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin-bottom: 18px; }
.kpi { background: var(--surface); border: 1px solid var(--hairline); border-radius: 14px; padding: 16px; display: flex; flex-direction: column; gap: 6px; }
.kl { font-size: 12px; text-transform: uppercase; letter-spacing: 0.04em; color: var(--muted); }
.kpi strong { font-family: var(--font-ui); font-size: 22px; }
.dgrid { display: grid; grid-template-columns: 1.4fr 1fr; gap: 18px; }
.card { background: var(--surface); border: 1px solid var(--hairline); border-radius: 14px; padding: 18px; }
.ct { font-size: 12px; text-transform: uppercase; letter-spacing: 0.04em; color: var(--muted); margin: 0 0 12px; }
.line { display: grid; grid-template-columns: 1fr auto auto; gap: 12px; padding: 8px 0; border-bottom: 1px solid var(--hairline); }
.clickrow { cursor: pointer; border-radius: 8px; } .clickrow:hover { background: var(--surface-off); }
</style>
